import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

async function fetchProductionIncidentList(queryString) {
  const url = `${QUALITY_SERVICE_API_URL}/hub/production-incidents?${queryString}`;
  const response = await api.get(url);
  return response.data;
}

// page/pageSize/sortBy/sortDir are accepted (and sent) but there's no pagination or
// sort-column UI yet — same minimal pattern as useServiceDisruptionOpen/useSupportBugsOpen,
// which likewise only ever request page 1 of the default page size.
function useProductionIncidentList(filters, dateRange, page = 1, pageSize = 50, sortBy = 'created', sortDir = 'desc') {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatDate = date => {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const fetchList = useCallback(async (isCancelled = () => false) => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      setData(null);
      setLoading(false);
      return;
    }

    const params = new URLSearchParams();
    ['domain', 'group', 'unit'].forEach(key => {
      (filters?.[key] || []).forEach(value => params.append(key, value));
    });

    const fromStr = formatDate(dateRange.startDate);
    if (fromStr) params.set('from', fromStr);
    const toStr = formatDate(dateRange.endDate);
    if (toStr) params.set('to', toStr);

    if (page) params.set('page', page);
    if (pageSize) params.set('pageSize', pageSize);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);

    const queryString = params.toString();

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const list = await fetchProductionIncidentList(queryString);
      if (!isCancelled()) setData(list);
    } catch (err) {
      if (!isCancelled()) setError(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [filters, dateRange, page, pageSize, sortBy, sortDir]);

  useEffect(() => {
    let cancelled = false;
    fetchList(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchList]);

  return { data, loading, error, refetch: () => fetchList() };
}

export default useProductionIncidentList;
